import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash, BookBookmark, ArrowsClockwise } from "@phosphor-icons/react";

const CATEGORIES = ["cabinet", "millwork", "hardware", "linear_footage", "material"];
const UNITS = ["ea", "lf", "sf", "bf", "pr"];

export default function Pricebook() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [draft, setDraft] = useState({ category: "material", name: "", unit: "ea", unit_cost: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pricebook");
      setItems(r.data);
    } catch {
      toast.error("Failed to load price book");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateItem = async (id, patch) => {
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  const saveItem = async (id) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    try {
      await api.put(`/pricebook/${id}`, { name: it.name, category: it.category, unit: it.unit, unit_cost: Number(it.unit_cost) });
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/pricebook/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const addItem = async () => {
    if (!draft.name.trim()) return toast.error("Name is required");
    try {
      const r = await api.post("/pricebook", { ...draft, unit_cost: Number(draft.unit_cost) });
      setItems((arr) => [r.data, ...arr]);
      setDraft({ category: "material", name: "", unit: "ea", unit_cost: 0 });
      toast.success("Entry added");
    } catch {
      toast.error("Add failed");
    }
  };

  const [syncing, setSyncing] = useState(false);
  const syncDefaults = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/pricebook/sync-defaults");
      const added = r.data?.added || 0;
      if (added > 0) {
        toast.success(`Added ${added} new default entr${added === 1 ? "y" : "ies"}`);
        await load();
      } else {
        toast.info("Already up to date — no new defaults to add");
      }
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const filtered = filter === "all" ? items : items.filter((x) => x.category === filter);

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="pricebook-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="data-label text-amber mb-1">Catalog</div>
          <h1 className="font-heading text-4xl tracking-tight font-medium">Price Book</h1>
          <p className="text-ink-400 mt-1 text-sm">{items.length} entries · used to auto-price AI takeoffs</p>
        </div>
        <Button
          onClick={syncDefaults}
          disabled={syncing}
          data-testid="sync-defaults-button"
          variant="ghost"
          className="border border-ink-700 hover:border-amber/60 hover:bg-ink-900 text-ink-400 hover:text-amber h-10 transition-colors"
          title="Add any new default entries that are missing — your customizations are preserved"
        >
          <ArrowsClockwise size={14} weight="bold" className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : "Sync defaults"}
        </Button>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="pricebook-filters">
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")} label="All" />
        {CATEGORIES.map((c) => (
          <FilterPill key={c} active={filter === c} onClick={() => setFilter(c)} label={c.replace("_", " ")} />
        ))}
      </div>

      {/* Add row */}
      <div className="border border-ink-700 bg-ink-900 rounded-md p-4 mb-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-2">
          <div className="data-label mb-1.5">Category</div>
          <select
            value={draft.category}
            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            className="w-full bg-ink-950 border border-ink-700 rounded-md h-10 px-3 text-sm focus:outline-none focus:border-amber transition-colors"
            data-testid="add-entry-category"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
        </div>
        <div className="md:col-span-5">
          <div className="data-label mb-1.5">Name</div>
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="3/4&quot; Plywood (Cherry)"
            className="bg-ink-950 border-ink-700 h-10"
            data-testid="add-entry-name"
          />
        </div>
        <div className="md:col-span-2">
          <div className="data-label mb-1.5">Unit</div>
          <select
            value={draft.unit}
            onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
            className="w-full bg-ink-950 border border-ink-700 rounded-md h-10 px-3 text-sm focus:outline-none focus:border-amber transition-colors num"
            data-testid="add-entry-unit"
          >
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <div className="data-label mb-1.5">Unit Cost ($)</div>
          <Input
            type="number"
            step="0.01"
            value={draft.unit_cost}
            onChange={(e) => setDraft({ ...draft, unit_cost: e.target.value })}
            className="bg-ink-950 border-ink-700 h-10 num text-right"
            data-testid="add-entry-cost"
          />
        </div>
        <div className="md:col-span-1">
          <Button
            onClick={addItem}
            data-testid="add-entry-button"
            className="w-full h-10 bg-amber hover:bg-amber-hover text-ink-950 font-semibold"
          >
            <Plus size={16} weight="bold" />
          </Button>
        </div>
      </div>

      <div className="border border-ink-700 bg-ink-900 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center data-label text-ink-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <BookBookmark size={36} weight="duotone" className="mx-auto text-ink-500 mb-3" />
            <div className="text-ink-400 text-sm">No entries in this category</div>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="pricebook-table">
            <thead>
              <tr className="bg-ink-800 border-b border-ink-700">
                <th className="text-left px-4 py-2 data-label">Category</th>
                <th className="text-left px-4 py-2 data-label">Name</th>
                <th className="text-left px-4 py-2 data-label">Unit</th>
                <th className="text-right px-4 py-2 data-label">Unit Cost ($)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-b border-ink-700/60 hover:bg-ink-800/40 transition-colors">
                  <td className="px-4 py-2">
                    <select
                      value={it.category}
                      onChange={(e) => updateItem(it.id, { category: e.target.value })}
                      onBlur={() => saveItem(it.id)}
                      className="bg-transparent text-xs uppercase tracking-wider text-ink-400 focus:outline-none focus:text-ink-50"
                    >
                      {CATEGORIES.map((c) => <option key={c} value={c} className="bg-ink-900">{c}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2 min-w-[300px]">
                    <input
                      value={it.name}
                      onChange={(e) => updateItem(it.id, { name: e.target.value })}
                      onBlur={() => saveItem(it.id)}
                      className="w-full bg-transparent text-ink-50 focus:outline-none focus:bg-ink-950 focus:px-2 rounded-sm"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <select
                      value={it.unit}
                      onChange={(e) => updateItem(it.id, { unit: e.target.value })}
                      onBlur={() => saveItem(it.id)}
                      className="bg-transparent text-xs num focus:outline-none"
                    >
                      {UNITS.map((u) => <option key={u} value={u} className="bg-ink-900">{u}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <input
                      type="number"
                      step="0.01"
                      value={it.unit_cost}
                      onChange={(e) => updateItem(it.id, { unit_cost: e.target.value })}
                      onBlur={() => saveItem(it.id)}
                      className="w-32 bg-transparent text-right num focus:outline-none focus:bg-ink-950 focus:px-2 rounded-sm"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => removeItem(it.id)}
                      className="text-ink-500 hover:text-red-400"
                      data-testid={`delete-pricebook-${it.id}`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 h-8 rounded-md border text-xs uppercase tracking-wider font-bold transition-colors duration-150 whitespace-nowrap ${
        active
          ? "bg-amber/10 border-amber/60 text-amber"
          : "bg-ink-900 border-ink-700 text-ink-400 hover:text-ink-50 hover:border-ink-500"
      }`}
    >
      {label}
    </button>
  );
}
